import React, { ReactElement } from 'react';
import { Trans } from 'react-i18next';
import { chain, includes, isEmpty, isNil, isString, memoize, size } from 'lodash';
import { isNumeric } from '../functions/lodashExtensions';
import { IntRange } from '../functions/numbers';
import { Code } from '../text/Code';

/**
 * VALID is a way of short-circuiting a validation to stop evaluating the remaining validators. So that e.g. a
 * ```
 * <TextInput validators={[validIfEmpty, email]} />
 * ```
 * can be valid both when it's either empty, or has a valid email.
 *
 * Note: a validator returns a falsy value when it's valid, and a truthy value when it's invalid.
 */
const VALID = Symbol('Validators.valid');

export type Validator<T> = (value?: T | null) => false | ReactElement | typeof VALID;

/** @deprecated For backwards compatibility with javascript, probably not needed with typescript. */
const string: Validator<string> = (value?: string | null) => {
  return !isString(value) && <Trans i18nKey="error.validation.string" />;
};

// note that isEmpty returns true for all numbers and booleans so this is not valid for numbers
function notEmpty<T>(value?: Exclude<T, number | boolean>): false | ReactElement {
  return isEmpty(value) && <Trans i18nKey="error.validation.notEmpty" />;
}

// note that isEmpty returns true for all numbers and booleans so this is not valid for numbers
function validIfEmpty<T>(value?: Exclude<T, number | boolean>): false | typeof VALID {
  return isEmpty(value) && VALID;
}

const length = (min: number, max: number): Validator<string> => {
  const { min: minLength, max: maxLength } = new IntRange({ min, max });
  // erroneously detected as a react component
  // eslint-disable-next-line react/display-name
  return (value?: string | null) => {
    // nil will be treated as length 0
    if (!isNil(value) && Validators.string(value)) {
      return Validators.string(value);
    }

    const length = size(value);

    return (
      (minLength && length < minLength && (
        <Trans i18nKey="error.validation.lengthTooShort" values={{ length, minLength }} />
      )) ||
      (maxLength && length > maxLength && (
        <Trans i18nKey="error.validation.lengthTooLong" values={{ length, maxLength }} />
      )) ||
      false
    );
  };
};

const allowedCharacters = (allowedCharactersRegex: RegExp): Validator<string> => {
  // erroneously detected as a react component
  // eslint-disable-next-line react/display-name
  return (value?: string | null) => {
    if (Validators.string(value)) {
      return Validators.string(value);
    }

    const disallowedCharacters = chain(value)
      .toArray()
      .uniq()
      .filter(char => !allowedCharactersRegex.test(char))
      .join('')
      .value();

    return (
      !isEmpty(disallowedCharacters) && (
        /* this is advanced usage of the Trans components which can be done in multiple ways, some more clear, others
        less. Read about it here: https://react.i18next.com/latest/trans-component. Note that some of the examples
        doesn't work due to TypeScript. The one used below is fairly straightforward too; we're replacing placeholder
        elements in the key with the dict given in components. values contains variables used and count determines if
        we will use the plural or singular form of the translation. */
        <Trans
          i18nKey="error.validation.allowedCharacters"
          values={{ allowedCharacters: allowedCharactersRegex.source, disallowedCharacters }}
          components={{ code: <Code /> }}
          count={size(disallowedCharacters)}
        />
      )
    );
  };
};

const email: Validator<string> = (value?: string | null) => {
  return (
    string(value) || ((isNil(value) || !value.match(/^.+@\S+(?<!\.)$/)) && <Trans i18nKey="error.validation.email" />)
  );
};

const number: Validator<string> = (value?: string | null) => {
  return (isNil(value) || !isNumeric(value) || includes(value, 'e')) && <Trans i18nKey="error.validation.number" />;
};

const max2FractionalDigits: Validator<string> = (value?: string | null) => {
  return (
    Validators.number(value) ||
    ((isNil(value) || value.length > Number(value).toFixed(2).length) && (
      <Trans i18nKey="error.validation.max2FractionDigits" />
    ))
  );
};

const maxInclusive = (maxValue: number): Validator<string> => {
  if (!isNumeric(maxValue)) {
    throw Error(`invalid maxValue ${maxValue}`);
  }
  // erroneously detected as a react component
  // eslint-disable-next-line react/display-name
  return value =>
    Validators.number(value) ||
    (Number(value) > Number(maxValue) && <Trans i18nKey="error.validation.maxInclusive" values={{ maxValue }} />);
};

const minInclusive = (minValue: number): Validator<string> => {
  if (!isNumeric(minValue)) {
    throw Error(`invalid minValue ${minValue}`);
  }
  // erroneously detected as a react component
  // eslint-disable-next-line react/display-name
  return value =>
    Validators.number(value) ||
    (Number(value) < Number(minValue) && <Trans i18nKey="error.validation.minInclusive" values={{ minValue }} />);
};

const notZero: Validator<string> = (value?: string | null) => {
  return Validators.number(value) || (Number(value) === 0 && <Trans i18nKey="error.validation.notZero" />);
};

const positive: Validator<string> = (value?: string | null) => {
  return Validators.number(value) || (Number(value) < 0 && <Trans i18nKey="error.validation.positive" />);
};

const negative: Validator<string> = (value?: string | null) => {
  return Validators.number(value) || (Number(value) > 0 && <Trans i18nKey="error.validation.negative" />);
};

const validate = <T,>(value: T | null | undefined, validators: Validator<T>[]): null | ReactElement => {
  const firstMatch = validators.find(it => it(value))?.(value);
  return firstMatch === VALID ? null : firstMatch || null;
};

const integer: Validator<string> = (value?: string | null) => {
  return (
    Validators.number(value) ||
    (!isNil(value) && Number(value).toFixed(0).length < value.length && <Trans i18nKey="error.validation.integer" />)
  );
};

const percentage: Validator<string> = (value?: string | null) => {
  return (
    Validators.number(value) ||
    ((Number(value) < 0 || Number(value) > 100) && <Trans i18nKey="error.validation.percentage" />)
  );
};

const Validators = {
  string,
  notEmpty,
  validIfEmpty,
  length: memoize(length, (min, max) => `${min},${max}`),
  allowedCharacters: memoize(allowedCharacters, allowedCharactersRegex => allowedCharactersRegex.source),
  email,
  number,
  max2FractionalDigits,
  maxInclusive: memoize(maxInclusive),
  minInclusive: memoize(minInclusive),
  notZero,
  positive,
  negative,
  validate,
  integer,
  percentage,
};

export { Validators };
